import { getRequiredFields } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { formValues, schema } from "./validations";
import {
  AMOUNT_1,
  AMOUNT_10,
  AMOUNT_11,
  AMOUNT_12,
  AMOUNT_2,
  AMOUNT_3,
  AMOUNT_4,
  AMOUNT_5,
  AMOUNT_6,
  AMOUNT_7,
  AMOUNT_8,
  AMOUNT_9,
} from "./consts";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetMonthDistributionByLineItemId } from "@/services";
import { toast } from "@/components/Toast";

const useMonthlyDistribution = ({
  annualBudget,
  onSave,
  setOpenDistributionDialog,
  rowSelected,
  draftDistributionData,
}: {
  annualBudget?: string;
  onSave?: (data: any) => void;
  setOpenDistributionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  rowSelected?: any;
  draftDistributionData?: any;
}) => {
  const { budgetId } = useParams();
  const [totalAmt, setTotalAmt] = useState(0);

  const form = useForm<formValues>({
    defaultValues: {
      [AMOUNT_1]: "0",
      [AMOUNT_2]: "0",
      [AMOUNT_3]: "0",
      [AMOUNT_4]: "0",
      [AMOUNT_5]: "0",
      [AMOUNT_6]: "0",
      [AMOUNT_7]: "0",
      [AMOUNT_8]: "0",
      [AMOUNT_9]: "0",
      [AMOUNT_10]: "0",
      [AMOUNT_11]: "0",
      [AMOUNT_12]: "0",
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const budgetData = useSelector(
    (state: RootState) => state.currentData.budgetData
  );

  const watchedValues = form.watch(); // watches all fields

  useEffect(() => {
    const total = Object.values(watchedValues).reduce((acc, val) => {
      const num = parseFloat(val);
      return acc + (isNaN(num) ? 0 : num);
    }, 0);

    setTotalAmt(total);
  }, [watchedValues]);

  const { data: monthlyDistribution, isLoading: isDistributionLoading } =
    useGetMonthDistributionByLineItemId({
      lineItemId: rowSelected?.budgetLineitemId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!rowSelected?.budgetLineitemId,
      },
    });

  useEffect(() => {
    if (draftDistributionData) {
      form.reset({
        [AMOUNT_1]: draftDistributionData?.[AMOUNT_1]?.toString() || "0",
        [AMOUNT_2]: draftDistributionData?.[AMOUNT_2]?.toString() || "0",
        [AMOUNT_3]: draftDistributionData?.[AMOUNT_3]?.toString() || "0",
        [AMOUNT_4]: draftDistributionData?.[AMOUNT_4]?.toString() || "0",
        [AMOUNT_5]: draftDistributionData?.[AMOUNT_5]?.toString() || "0",
        [AMOUNT_6]: draftDistributionData?.[AMOUNT_6]?.toString() || "0",
        [AMOUNT_7]: draftDistributionData?.[AMOUNT_7]?.toString() || "0",
        [AMOUNT_8]: draftDistributionData?.[AMOUNT_8]?.toString() || "0",
        [AMOUNT_9]: draftDistributionData?.[AMOUNT_9]?.toString() || "0",
        [AMOUNT_10]: draftDistributionData?.[AMOUNT_10]?.toString() || "0",
        [AMOUNT_11]: draftDistributionData?.[AMOUNT_11]?.toString() || "0",
        [AMOUNT_12]: draftDistributionData?.[AMOUNT_12]?.toString() || "0",
      });
    } else if (monthlyDistribution) {
      form.reset({
        [AMOUNT_1]: monthlyDistribution?.data?.[AMOUNT_1]?.toString() || "0",
        [AMOUNT_2]: monthlyDistribution?.data?.[AMOUNT_2]?.toString() || "0",
        [AMOUNT_3]: monthlyDistribution?.data?.[AMOUNT_3]?.toString() || "0",
        [AMOUNT_4]: monthlyDistribution?.data?.[AMOUNT_4]?.toString() || "0",
        [AMOUNT_5]: monthlyDistribution?.data?.[AMOUNT_5]?.toString() || "0",
        [AMOUNT_6]: monthlyDistribution?.data?.[AMOUNT_6]?.toString() || "0",
        [AMOUNT_7]: monthlyDistribution?.data?.[AMOUNT_7]?.toString() || "0",
        [AMOUNT_8]: monthlyDistribution?.data?.[AMOUNT_8]?.toString() || "0",
        [AMOUNT_9]: monthlyDistribution?.data?.[AMOUNT_9]?.toString() || "0",
        [AMOUNT_10]: monthlyDistribution?.data?.[AMOUNT_10]?.toString() || "0",
        [AMOUNT_11]: monthlyDistribution?.data?.[AMOUNT_11]?.toString() || "0",
        [AMOUNT_12]: monthlyDistribution?.data?.[AMOUNT_12]?.toString() || "0",
      });
    } else if (annualBudget) {
      const divide = parseInt(annualBudget) / 12;
      const amt = parseFloat(divide.toString()).toFixed(2);
      form.reset({
        [AMOUNT_1]: amt?.toString(),
        [AMOUNT_2]: amt?.toString(),
        [AMOUNT_3]: amt?.toString(),
        [AMOUNT_4]: amt?.toString(),
        [AMOUNT_5]: amt?.toString(),
        [AMOUNT_6]: amt?.toString(),
        [AMOUNT_7]: amt?.toString(),
        [AMOUNT_8]: amt?.toString(),
        [AMOUNT_9]: amt?.toString(),
        [AMOUNT_10]: amt?.toString(),
        [AMOUNT_11]: amt?.toString(),
        [AMOUNT_12]: amt?.toString(),
      });
    }
  }, [annualBudget, monthlyDistribution, draftDistributionData]);

  const onClear = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    form.reset({
      [AMOUNT_1]: "0",
      [AMOUNT_2]: "0",
      [AMOUNT_3]: "0",
      [AMOUNT_4]: "0",
      [AMOUNT_5]: "0",
      [AMOUNT_6]: "0",
      [AMOUNT_7]: "0",
      [AMOUNT_8]: "0",
      [AMOUNT_9]: "0",
      [AMOUNT_10]: "0",
      [AMOUNT_11]: "0",
      [AMOUNT_12]: "0",
    });
  };

  const onSubmit = (data: any) => {
    if (Math.round(Number(totalAmt)) != Number(annualBudget as string)) {
      toast.error("Total should be same as Annual Budget");
      return;
    }
    onSave && onSave(data);
    onCloseDialog();
  };

  const onCloseDialog = () => {
    setOpenDistributionDialog(false);
  };

  return {
    budgetData,
    form,
    requiredFields,
    onSubmit,
    totalAmt,
    onClear,
    onCloseDialog,
    isDistributionLoading,
  };
};

export default useMonthlyDistribution;
